import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min';
import '../assets/styles/Home.css';
import { useLanguage } from '../context/LanguageContext';

// Import des images
import home_image from '../assets/images/home_image.png';
import mission_vision_image from '../assets/images/mission_vision.png';
import key_figures_image from '../assets/images/key_figures.png';
import services_image from '../assets/images/services.png';
import portrait_popup from '../assets/images/portrait_popup.jpg'; // Import de l'image portrait

const Home = () => {
  const { language } = useLanguage();
  const [showPopup, setShowPopup] = useState(false);

  // Afficher le popup après le chargement de la page
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowPopup(true);
    }, 2000); // Affiche après 2 secondes

    return () => clearTimeout(timer);
  }, []);

  // Fermer le popup
  const closePopup = () => {
    setShowPopup(false);
  };

  // Empêcher la fermeture en cliquant sur le contenu du popup
  const handlePopupClick = (e) => {
    e.stopPropagation();
  };

  // Contenu en fonction de la langue
  const content = {
    fr: {
      // Hero Section
      heroTitle: "Bienvenue sur K'MDEU FARMS SARL",
      heroSubtitle: "Manger toujours qualité",
      heroButton: "Découvrez nos produits",

      // Popup content
      popupTitle: "Bienvenue chez K'MDEU FARMS",
      popupMessage: "Découvrez notre engagement pour une agriculture durable et de qualité",
      popupButton: "Explorer",

      // ... (le reste de votre contenu français reste inchangé)
      aboutTitle: "Bienvenue chez K'mdeu Farms Sarl",
      aboutSubtitle: "À Propos de Notre Entreprise",
      aboutText1: "Nous sommes une entreprise agricole dynamique spécialisée dans la production végétale et animale. Nous aidons également les agriculteurs établis à améliorer leurs rendements et leur expansion, en utilisant nos services et programmes de soutien uniques et adaptés aux réalités locales.",
      aboutText2: "Nous aidons les personnes qui aspirent à devenir agriculteurs à créer leur ferme depuis le début. Nous assurons la gestion de projets en mobilisant notre gamme d'experts pour garantir des bases solides et un développement durable.",
      aboutText3: "Nous sommes situés à Douala, dans la région du Littoral, et nous avons construit, au cours des 8 dernières années, une solide fondation pour porter l'agriculture à un niveau supérieur au Cameroun et au-delà.",

      // Mission & Vision
      missionVisionTitle: "Notre Vision et Mission",
      visionTitle: "Notre Vision",
      visionText: "Nous aspirons à être le leader de la transformation de l'Afrique à travers une agriculture saine et à garantir la souveraineté alimentaire en nous appuyant sur de solides atouts de développement culturel et communautaire.",
      missionTitle: "Notre Mission",
      missionItems: [
        "Assurer une plateforme de production de cultures et d'élevage saine et suffisante pour la population croissante de l'Afrique dans les décennies à venir.",
        "Renforcer les capacités des petits agriculteurs grâce à des efforts de mutualisation et des programmes d'amélioration des rendements, tout en construisant une plateforme de co-apprentissage pour les acteurs."
      ],

      // Key Figures
      keyFiguresTitle: "Chiffres Clés",
      figures: [
        {
          number: "+50",
          title: "hectares de facilités et infrastructure",
          text: "dans la région du Littoral où nos activités de R&D se passent. C'est le coeur de notre travail de recherche et de mise en expérimentation."
        },
        {
          number: "+700",
          title: "Force de capital de production",
          text: "dans notre centre de recherche principal avec une projection de +2,000 d'ici 2026 et de supporter la production de notre réseau de producteurs pour atteindre +30,000 d'ici 2030."
        },
        {
          number: "04",
          title: "Pays dans 2 régions",
          text: "avec les atouts de facilités pour l'extension de nos services et programmes, et la mise en place d'un réseau de producteurs à la hauteur de nos ambitions: Cameroon, Gabon, Côte d'Ivoire, Burkina Faso."
        },
        {
          number: "+1000",
          title: "Agriculteurs dans notre base de données",
          text: "devant bénéficier de nos différents services et soutenir leurs objectifs. Donner tous les producteurs sur une plateforme unique afin de profiter des efforts mutualisés."
        }
      ],

      // Products & Services
      productsTitle: "Nos Produits & Services",
      productionTitle: "1. Production et Distribution d'Aliments Sains",
      productionText: "Nous tirons parti de nos installations de R&D pour produire des cultures de haute qualité et une grande variété en utilisant des espaces naturels (sans produits chimiques). Nous produisons également du porc et de la volaille de qualité pour une consommation de masse.",
      servicesTitle: "2. Services de Soutien aux Agriculteurs à Travers l'Afrique",
      servicesIntro: "Il existe de nombreux défis dans le secteur agricole en Afrique, et notre objectif est de fournir des solutions sur mesure pour remplir notre mission. En réponse aux défis identifiés, nous proposons :",
      serviceItems: [
        {
          title: "a) Outils pour les Petits Agriculteurs",
          text: "La majorité de notre réseau d'agriculteurs est constituée de petits exploitants ayant un accès limité aux machines et aux outils. Grâce à des efforts mutualisés et aux divers partenariats établis avec les fabricants, nous fournissons de petits outils pour la mécanisation, qui changent la vie de nos agriculteurs."
        },
        {
          title: "b) Fournitures d'aliments de Qualité",
          text: "En raison de l'inconsistance de l'approvisionnement en aliments et de la qualité, ainsi que de la disponibilité limitée d'ingrédients de qualité qui affectent les taux de croissance des porcs et leur développement global, nous sommes déterminés à soutenir nos agriculteurs avec des aliments de qualité adaptés à leur environnement."
        },
        {
          title: "c) Gestion des Maladies",
          text: "Pour faire face aux épidémies de maladies porcines et à l'accès limité aux soins vétérinaires, nous organisons des processus vétérinaires avec notre réseau de partenaires vétérinaires, et nous mettons en œuvre des efforts dynamiques de contrôle des maladies."
        },
        {
          title: "d) Accès au Marché",
          text: "Nous aidons nos producteurs à accéder au marché pour leur production, soit en les connectant aux consommateurs finaux, soit en distribuant à travers notre réseau, en tirant parti de la technologie et de notre approche innovante."
        }
      ],
      consultingTitle: "3. Gestion de Projets et Conseil",
      consultingText: "Nous soutenons les agriculteurs établis à développer et à étendre leurs activités tout en améliorant la productivité. Nous aidons également les personnes passionnées à devenir agriculteurs en les soutenant dans l'établissement de leur ferme depuis le début. Cela implique la conception de projets, le financement, la mise en œuvre et un suivi approfondi.",

      // Team Section
      teamTitle: "Notre Équipe",
      teamDescription: "Notre équipe est composée de professionnels passionnés et dévoués, travaillant ensemble pour atteindre nos objectifs et offrir le meilleur à nos clients.",

      // Gallery Sections
      photoTitle: "Photothèque",
      videoTitle: "Vidéothèque",
      podcastTitle: "Nos PodCasts",
      seeMore: "Voir plus"
    },
    en: {
      // Hero Section
      heroTitle: "Welcome to K'MDEU FARMS SARL",
      heroSubtitle: "Always eat quality",
      heroButton: "Discover our products",

      // Popup content
      popupTitle: "Welcome to K'MDEU FARMS",
      popupMessage: "Discover our commitment to sustainable and quality agriculture",
      popupButton: "Explore",

      // ... (le reste de votre contenu anglais reste inchangé)
      aboutTitle: "Welcome to K'mdeu Farms Sarl",
      aboutSubtitle: "About Our Company",
      aboutText1: "We are a dynamic agricultural company specializing in both crop and livestock production. We also help established farmers to improve yield and their expansion, using our unique tailored support services and program, adapted to local realities.",
      aboutText2: "We help people that aspire to become farmers across Africa to set up their farm from scratch. We project manage using our range of experts to ensure solid foundation and development.",
      aboutText3: "We are Located in Douala in Littoral Region and have been building for the past 8 years a strong foundation to take Agriculture to another level in Cameroon and beyond.",

      // Mission & Vision
      missionVisionTitle: "Our Vision and Mission",
      visionTitle: "Our Vision",
      visionText: "We aspire to be the leader in the transformation of Africa through healthy agriculture and ensuring food sovereignty by building on strong cultural and community development assets.",
      missionTitle: "Our Mission",
      missionItems: [
        "Ensuring a healthy and sufficient crop and livestock production platform for Africa's growing population in the coming decades.",
        "Capacity building of small holders farmers with pooling efforts and yield improvement programs as well building co-learning platform for the actors."
      ],

      // Key Figures
      keyFiguresTitle: "Key Figures",
      figures: [
        {
          number: "+50",
          title: "hectares facilities",
          text: "in Littoral Region where all R&D activities are taking place. From ideas to prototype and concept validation."
        },
        {
          number: "+700",
          title: "Pig capacity production",
          text: "in our main R&D facility with projection to achieve 2,000 by 2026 and support production for our network farmers to achieve +30,000 by 2030."
        },
        {
          number: "04",
          title: "Countries with assets",
          text: "and foundation for the extension of our R&D efforts and activities, especially for pig and poultry farm development: Cameroon, Gabon, Côte d'Ivoire, Burkina Faso."
        },
        {
          number: "+1000",
          title: "Farmers currently in our database",
          text: "to benefit our services and support program. Plan is to get all farmers into unique platform to leverage on mutualized efforts."
        }
      ],

      // Products & Services
      productsTitle: "Our Products & Services",
      productionTitle: "1. Healthy Food Production and Distribution",
      productionText: "We leverage our R&D facilities to produce high-quality crops and a wide variety using natural spaces (without chemicals). We also produce quality pork and poultry for mass consumption.",
      servicesTitle: "2. Farmer Support Services Across Africa",
      servicesIntro: "There are many challenges in the agricultural sector in Africa, and our goal is to provide tailored solutions to fulfill our mission. In response to identified challenges, we offer:",
      serviceItems: [
        {
          title: "a) Tools for Small Farmers",
          text: "The majority of our farmer network consists of smallholders with limited access to machinery and tools. Through pooled efforts and various partnerships with manufacturers, we provide small tools for mechanization that change our farmers' lives."
        },
        {
          title: "b) Quality Feed Supplies",
          text: "Due to inconsistent feed supply and quality, as well as limited availability of quality ingredients that affect pig growth rates and their overall development, we are committed to supporting our farmers with quality feed suitable for their environment."
        },
        {
          title: "c) Disease Management",
          text: "To address swine disease outbreaks and limited access to veterinary care, we organize veterinary processes with our network of veterinary partners, and we implement dynamic disease control efforts."
        },
        {
          title: "d) Market Access",
          text: "We help our producers access the market for their production, either by connecting them to end consumers or distributing through our network, leveraging technology and our innovative approach."
        }
      ],
      consultingTitle: "3. Project Management and Consulting",
      consultingText: "We support established farmers to develop and expand their activities while improving productivity. We also help passionate people become farmers by supporting them in establishing their farm from the beginning. This involves project design, financing, implementation and thorough monitoring.",

      // Team Section
      teamTitle: "Our Team",
      teamDescription: "Our team is made up of passionate and dedicated professionals, working together to achieve our goals and offer the best to our customers.",

      // Gallery Sections
      photoTitle: "Photo Gallery",
      videoTitle: "Video Gallery",
      podcastTitle: "Our Podcasts",
      seeMore: "See more"
    }
  };

  const currentContent = content[language];

  const photos = [
    { src: '/images/photo1.jpg', caption: language === 'fr' ? 'Identification zone de culture' : 'Crop zone identification' },
    { src: '/images/recolte_mais.jpg', caption: language === 'fr' 
      ? 'Chargement de notre récolte de maïs' 
      : 'Loading our maize harvest' },
    { src: '/images/recolte.jpg', caption: language === 'fr' ? 'Récolte banane plantain' : 'Plantain banana harvest' },
  ];



 const videos = [
  { 
    src: '/videos/recolte_mais_bertoua.mp4', 
    caption: language === 'fr' 
      ? 'Chargement de notre récolte de maïs' 
      : 'Loading our maize harvest' 
  },
  { 
    src: '/videos/egrenage_mais.mp4', 
    caption: language === 'fr' 
      ? 'Égrenage du maïs fraîchement récolté' 
      : 'Shelling freshly harvested maize' 
  },
  { 
    src: '/videos/truie_allerte.mp4', 
    caption: language === 'fr' 
      ? 'Truie appelant ses porcelets' 
      : 'Sow calling her piglets' 
  },
];


 const podcasts = [
  { 
    title: language === 'fr' 
      ? 'Sa ferme compte aujourd’hui plus de 400 porcs, partis de seulement 15 en trois ans' 
      : 'His farm now has over 400 pigs, starting from just 15 in three years',
    description: language === 'fr' 
      ? 'Découvrez comment cet éleveur passionné a développé son élevage de porcs de 15 à plus de 400 en un temps record grâce à son savoir-faire et sa persévérance.' 
      : 'Discover how this passionate farmer grew his pig farm from 15 to over 400 in just three years through skill and perseverance.',
    url: 'https://www.youtube.com/embed/nVQuwwI3pho'
  },
  { 
    title: language === 'fr' 
      ? 'PASCAL PEUMENI : apprendre sans relâche, même après 5 ans d’expérience, une passion pour l’agropastoralisme' 
      : 'PASCAL PEUMENI: Continuous learning after 5 years of experience, driven by a passion for agro-pastoralism',
    description: language === 'fr' 
      ? 'Rencontrez Pascal Peumeni, un éleveur et entrepreneur agricole qui continue d’apprendre et d’innover chaque jour dans le domaine de l’agropastoralisme.' 
      : 'Meet Pascal Peumeni, a dedicated farmer and agripreneur who keeps learning and innovating every day in the field of agro-pastoralism.',
    url: 'https://www.youtube.com/embed/wC7s1kYb1x4'
  },
  { 
    title: language === 'fr' 
      ? 'Il réalise des analyses de sol à partir d’images satellites' 
      : 'He performs soil analysis using satellite imagery',
    description: language === 'fr' 
      ? 'Découvrez comment la technologie satellitaire lui permet d’analyser l’état des sols à distance pour mieux comprendre la fertilité et la santé des cultures.' 
      : 'Learn how satellite technology allows him to analyze soil health remotely and improve crop productivity.',
    url: 'https://www.youtube.com/embed/ObQZ2NycHZQ?si=nlMaHXECfGk2ryWW'
  },
];


  const teamMembers = [
    { name: 'PEUMENI Pascal', role: language === 'fr' ? 'Associé Gérant' : 'Managing Partner', photo: 'path/to/pascal.jpg', linkedin: 'https://www.linkedin.com/in/pascal-peumeni' },
    { name: 'NGUEUMENI Joseph Berlin', role: language === 'fr' ? 'Associé Gérant' : 'Managing Partner', photo: 'path/to/berlin.jpg', linkedin: 'https://www.linkedin.com/in/berlin-ngueumeni' },
  ];

  // Fonction pour faire défiler vers les sections
  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="home-page">
      {/* Popup de bienvenue */}
      {showPopup && (
        <div className="popup-overlay" onClick={closePopup}>
          <div className="popup-content" onClick={handlePopupClick}>
            <button className="popup-close" onClick={closePopup}>
              ×
            </button>
            
            <div className="popup-body">
              <div className="popup-image-container">
                <img 
                  src={portrait_popup} 
                  alt="K'MDEU FARMS" 
                  className="popup-portrait"
                />
              </div>
              
              <div className="popup-text">
                <h3 className="popup-title">{currentContent.popupTitle}</h3>
                <p className="popup-message">{currentContent.popupMessage}</p>
                <button 
                  className="btn btn-success popup-action-btn"
                  onClick={closePopup}
                >
                  {currentContent.popupButton}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Section Hero */}
      <section className="hero-section">
        <div className="container text-center">
          <h1 className="display-4">{currentContent.heroTitle}</h1>
          <p className="lead">{currentContent.heroSubtitle}</p>
          <button
            className="btn btn-lg btn-warning"
            onClick={() => scrollToSection('products-section')}
          >
            {currentContent.heroButton}
          </button>
        </div>
      </section>

      {/* Le reste de votre code Home.js reste inchangé */}
      {/* Section Présentation de l'entreprise */}
      <section id="about-section" className="about-section py-5">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-md-6 mb-4 mb-md-0">
              <h2>{currentContent.aboutTitle}</h2>
              <h4 className="text-success">{currentContent.aboutSubtitle}</h4>
              <p className="text-dark">
                {currentContent.aboutText1}
              </p>
              <p className="text-dark">
                {currentContent.aboutText2}
              </p>
              <p className="text-dark">
                {currentContent.aboutText3}
              </p>
            </div>
            <div className="col-md-6">
              <div className="image-container-large">
                <img src={home_image} alt="Welcome kmdeu" className="img-fluid rounded shadow" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section Mission et Vision avec image à droite */}
      <section className="mission-vision-section py-5 bg-light">
        <div className="container">
          <h2 className="text-center text-success mb-5">{currentContent.missionVisionTitle}</h2>
          <div className="row align-items-center">
            <div className="col-md-6 mb-4 mb-md-0">
              <div className="vision-mission-content">
                <div className="mb-4">
                  <h3 className="text-success">{currentContent.visionTitle}</h3>
                  <p className="text-dark">
                    {currentContent.visionText}
                  </p>
                </div>
                
                <div>
                  <h3 className="text-success">{currentContent.missionTitle}</h3>
                  <ul className="text-dark">
                    {currentContent.missionItems.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="image-container-large text-center">
                <img 
                  src={mission_vision_image} 
                  alt="Vision and Mission" 
                  className="img-fluid rounded shadow"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section Chiffres Clés avec image à gauche */}
      <section className="key-figures-section py-5">
        <div className="container">
          <h2 className="text-center text-success mb-5">{currentContent.keyFiguresTitle}</h2>
          <div className="row align-items-center">
            <div className="col-md-4 mb-4 mb-md-0">
              <div className="image-container-large text-center">
                <img 
                  src={key_figures_image} 
                  alt="Key Figures" 
                  className="img-fluid rounded shadow"
                />
              </div>
            </div>
            <div className="col-md-8">
              <div className="key-figures-vertical">
                {currentContent.figures.map((figure, index) => (
                  <div key={index} className="key-figure-item mb-5">
                    <div className="row align-items-center">
                      <div className="col-2 text-center">
                        <div className="key-figure-number">
                          <h3 className="text-warning display-6">{figure.number}</h3>
                        </div>
                      </div>
                      <div className="col-10">
                        <div className="key-figure-content">
                          <h5 className="text-success">{figure.title}</h5>
                          <p className="text-dark mb-0">{figure.text}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section Produits et Services avec image à droite */}
      <section id="products-section" className="products-services-section py-5 bg-light">
        <div className="container">
          <h2 className="text-center text-success mb-5">{currentContent.productsTitle}</h2>
          <div className="row align-items-center">
            <div className="col-md-7 mb-4 mb-md-0">
              <div className="services-content">
                <div className="mb-4">
                  <h4 className="text-success">{currentContent.productionTitle}</h4>
                  <p className="text-dark">
                    {currentContent.productionText}
                  </p>
                </div>

                <div className="mb-4">
                  <h4 className="text-success">{currentContent.servicesTitle}</h4>
                  <p className="text-dark">
                    {currentContent.servicesIntro}
                  </p>
                  
                  <div className="service-items mt-3">
                    {currentContent.serviceItems.map((service, index) => (
                      <div key={index} className="service-item mb-3">
                        <h5 className="text-warning">{service.title}</h5>
                        <p className="text-dark">
                          {service.text}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="text-success">{currentContent.consultingTitle}</h4>
                  <p className="text-dark">
                    {currentContent.consultingText}
                  </p>
                </div>
              </div>
            </div>
            <div className="col-md-5">
              <div className="image-container-large text-center">
                <img 
                  src={services_image} 
                  alt="Products and Services" 
                  className="img-fluid rounded shadow"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section Notre Équipe */}
      <section className="team-section py-5">
        <div className="container">
          <h2 className="text-center text-success mb-5">{currentContent.teamTitle}</h2>
          <p className="text-center mb-4">
            {currentContent.teamDescription}
          </p>
          <div className="row justify-content-center">
            {teamMembers.map((member, index) => (
              <div key={index} className="col-md-6 text-center mb-4">
                <div className="team-member-card p-4">
                  <img src={member.photo} alt={member.name} className="img-fluid rounded-circle mb-3" style={{ width: '150px', height: '150px', objectFit: 'cover' }} />
                  <h3 className="text-success">{member.name}</h3>
                  <p className="text-dark">{member.role}</p>
                  <a href={member.linkedin} target="_blank" rel="noopener noreferrer" className="btn btn-outline-primary btn-sm">
                    LinkedIn
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section Phototeques */}
      <section className="phototeques-section py-5 bg-light">
        <div className="container">
          <h2 className="text-center text-success mb-5">{currentContent.photoTitle}</h2>
          <div className="row text-center">
            {photos.map((photo, index) => (
              <div key={index} className="col-md-4 mb-4">
                <div className="image-container">
                  <img src={photo.src} alt={photo.caption} className="img-fluid fixed-size-image rounded shadow" />
                </div>
                <p className="mt-3">{photo.caption}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-4">
            <Link to="/phototheques" className="btn btn-primary">{currentContent.seeMore}</Link>
          </div>
        </div>
      </section>

      {/* Section Vidéothèque */}
      <section className="videos-section py-5">
        <div className="container">
          <h2 className="text-center text-success mb-5">{currentContent.videoTitle}</h2>
          <div className="row text-center">
            {videos.map((video, index) => (
              <div key={index} className="col-md-4 mb-4">
                <div className="video-container">
                  <video controls className="fixed-size-video rounded shadow">
                    <source src={video.src} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
                <p className="mt-3">{video.caption}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-4">
            <Link to="/videotheques" className="btn btn-primary">{currentContent.seeMore}</Link>
          </div>
        </div>
      </section>

      {/* Section Podcasts */}
      <section className="podcasts-section py-5 bg-light">
        <div className="container">
          <h2 className="text-center text-success mb-5">{currentContent.podcastTitle}</h2>
          <div className="row text-center">
            {podcasts.map((podcast, index) => (
              <div key={index} className="col-md-4 mb-4">
                <div className="card h-100 shadow">
                  <div className="ratio ratio-16x9">
                    <iframe 
                      src={podcast.url} 
                      title="YouTube video player" 
                      frameBorder="0" 
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                      allowFullScreen
                    ></iframe>
                  </div>
                  <div className="card-body">
                    <h3 className="text-success">{podcast.title}</h3>
                    <p className="text-dark">{podcast.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-4">
            <Link to="/podcasts" className="btn btn-primary">{currentContent.seeMore}</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;